import { useEffect, useRef, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { Bell, CheckCircle2 } from 'lucide-react';

import type { ActionGroup } from '@/api/schema';
import { Spinner } from '@/components/ui/Spinner';
import { cx } from '@/lib/cx';

import { useActionsDue } from '../api';
import styles from './NotificationBell.module.css';

/** Three buckets, in the order somebody should work through them. */
const SECTIONS: { urgency: ActionGroup['urgency']; title: string }[] = [
  { urgency: 'overdue', title: 'Overdue' },
  { urgency: 'today', title: 'Today' },
  { urgency: 'follow_up', title: 'Follow up' },
];

/**
 * The bell, and what is behind it.
 *
 * Every count comes from the server, computed on each read. Nothing is stored and there
 * is nothing to mark read. An item disappears because the work behind it was done, not
 * because somebody dismissed it. That is the difference between a to-do list and an
 * inbox, and this is meant to be the former.
 *
 * Every row navigates to the screen that fixes it. A notification you cannot act on is
 * just worry.
 */
export function NotificationBell() {
  const navigate = useNavigate();
  const actions = useActionsDue();

  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const total = actions.data?.total ?? 0;
  const groups = actions.data?.groups ?? [];

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        type="button"
        className={styles.bell}
        aria-label={total > 0 ? `Notifications, ${total} due` : 'Notifications'}
        aria-expanded={open}
        onClick={() => {
          setOpen((was) => !was);
        }}
      >
        <Bell size={17} />
        {total > 0 ? (
          // Capped in the label rather than the number: "99+" is honest about being a
          // lot, where a four-digit badge is just noise.
          <span className={styles.badge}>{total > 99 ? '99+' : total}</span>
        ) : null}
      </button>

      {open ? (
        <div className={styles.panel} role="dialog" aria-label="Actions due">
          <p className={styles.head}>Actions due</p>

          {actions.isPending ? (
            <p className={styles.empty}>
              <Spinner size={14} label="Loading" />
            </p>
          ) : actions.isError ? (
            <p className={styles.empty}>Those did not load.</p>
          ) : groups.length === 0 ? (
            <p className={styles.clear}>
              <CheckCircle2 size={18} aria-hidden="true" />
              Nothing needs doing.
            </p>
          ) : (
            SECTIONS.map((section) => {
              const inSection = groups.filter((group) => group.urgency === section.urgency);
              if (inSection.length === 0) return null;
              return (
                <section key={section.urgency} className={styles.section}>
                  <h3 className={cx(styles.sectionTitle, styles[section.urgency])}>
                    {section.title}
                  </h3>
                  <ul className={styles.list}>
                    {inSection.map((group) => (
                      <li key={group.key}>
                        <button
                          type="button"
                          className={styles.item}
                          onClick={() => {
                            setOpen(false);
                            void navigate(group.route);
                          }}
                        >
                          <span className={styles.itemTitle}>{group.title}</span>
                          <span className={styles.itemCount}>{group.count}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}
